import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const ProductPreview = () => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPreviewData();
  }, []);

  const loadPreviewData = () => {
    try {
      const previewData = localStorage.getItem('product_preview');
      if (previewData) {
        const parsedData = JSON.parse(previewData);
        setProduct(parsedData);
        console.log('预览数据加载成功:', parsedData);
      } else {
        setError('未找到预览数据。请先在产品编辑器中保存草稿并点击预览按钮。');
      }
    } catch (error) {
      console.error('加载预览数据失败:', error);
      setError('加载预览数据失败，数据格式可能不正确。');
    } finally {
      setLoading(false);
    }
  };

  // 解析富文本JSON数据
  const parseRichTextContent = (jsonData) => {
    if (!jsonData) return '';
    
    try {
      if (typeof jsonData === 'string') {
        const parsed = JSON.parse(jsonData);
        return parsed.content || '';
      } else if (typeof jsonData === 'object') {
        return jsonData.content || '';
      }
      return jsonData || '';
    } catch {
      // 如果不是JSON格式，直接返回原值
      return jsonData || '';
    }
  };

  // 安全地渲染HTML内容
  const renderHTML = (htmlContent) => {
    if (!htmlContent) return null;
    
    return (
      <div 
        className="prose max-w-none"
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-gray-50">
        <div className="container-max section-padding">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2 mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="h-96 bg-gray-300 rounded"></div>
              <div className="space-y-4">
                <div className="h-6 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded w-3/4"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-20 min-h-screen bg-gray-50">
        <div className="container-max section-padding text-center">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-tech mb-2">预览加载失败</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/admin/products" className="btn-primary">
            返回产品管理
          </Link>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-20 min-h-screen bg-gray-50">
        <div className="container-max section-padding text-center">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-tech mb-2">预览数据为空</h2>
          <p className="text-gray-600 mb-6">请先在产品编辑器中填写内容并点击预览按钮</p>
          <Link to="/admin/products" className="btn-primary">
            返回产品管理
          </Link>
        </div>
      </div>
    );
  }

  // 解析富文本字段
  const featuresContent = parseRichTextContent(product.features);
  const specificationsContent = parseRichTextContent(product.specifications);
  const applicationsContent = parseRichTextContent(product.applications);
  const supportInfoContent = parseRichTextContent(product.support_info);
  const priceInfoContent = parseRichTextContent(product.price_info);

  return (
    <div className="pt-20 min-h-screen bg-gray-50">
      <div className="container-max section-padding">
        {/* 预览标识 */}
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-yellow-800 font-medium">产品预览模式</span>
            </div>
            <div className="text-sm text-yellow-700">
              预览ID: {product.previewId || 'N/A'} | 预览时间: {product.previewTime ? new Date(product.previewTime).toLocaleString('zh-CN') : 'N/A'}
            </div>
          </div>
          <p className="mt-2 text-sm text-yellow-700">
            这是产品的预览页面，数据来自本地草稿。此页面仅用于预览，产品尚未发布到网站。
          </p>
        </div>

        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm text-gray-600">
            <li>
              <Link to="/" className="hover:text-primary transition-colors">首页</Link>
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </li>
            <li>
              <Link to="/products" className="hover:text-primary transition-colors">产品发布</Link>
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </li>
            <li className="text-primary font-medium">{product.name || '未命名产品'}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div>
            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-4">
              {product.image ? (
                typeof product.image === 'string' ? (
                  <img
                    src={product.image}
                    alt={product.name || '产品图片'}
                    className="w-full h-96 object-cover"
                  />
                ) : (
                  <div className="w-full h-96 bg-gray-100 flex items-center justify-center">
                    <div className="text-center">
                      <svg className="w-16 h-16 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-500">已选择图片文件</p>
                      <p className="text-sm text-gray-400">{product.image.name || '图片文件'}</p>
                    </div>
                  </div>
                )
              ) : (
                <div className="w-full h-96 bg-gray-100 flex items-center justify-center">
                  <div className="text-center">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-gray-500">暂无产品图片</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="mb-4">
                {product.category && (
                  <span className="bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                    {product.category}
                  </span>
                )}
              </div>
              
              <h1 className="text-3xl font-bold text-gray-tech mb-4">
                {product.name || '未命名产品'}
              </h1>
              
              <p className="text-gray-600 text-lg mb-6">
                {product.description || '暂无产品描述'}
              </p>

              {/* 价格信息预览 */}
              {priceInfoContent && (
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-gray-tech mb-3">价格信息</h3>
                  <div className="text-gray-600">
                    {renderHTML(priceInfoContent)}
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <p className="text-sm text-gray-500">
                  预览状态: {product.isPreview ? '预览中' : '未知'} | 
                  最后更新: {product.previewTime ? new Date(product.previewTime).toLocaleDateString('zh-CN') : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details Tabs */}
        <div className="mt-12">
          <div className="bg-white rounded-lg shadow-md">
            {/* Tab Navigation */}
            <div className="border-b">
              <nav className="flex overflow-x-auto">
                {[
                  { id: 'overview', name: '产品概述' },
                  { id: 'features', name: '产品特点' },
                  { id: 'specifications', name: '技术规格' },
                  { id: 'applications', name: '应用领域' },
                  { id: 'support', name: '支持信息' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 px-6 py-4 font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'overview' && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-tech mb-4">产品概述</h3>
                  <div className="text-gray-600 leading-relaxed">
                    {product.full_description ? (
                      renderHTML(product.full_description)
                    ) : (
                      <p className="text-gray-500 italic">暂无产品概述</p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'features' && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-tech mb-4">产品特点</h3>
                  <div className="text-gray-600">
                    {featuresContent ? (
                      renderHTML(featuresContent)
                    ) : (
                      <p className="text-gray-500 italic">暂无产品特点</p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'specifications' && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-tech mb-4">技术规格</h3>
                  <div className="text-gray-600">
                    {specificationsContent ? (
                      renderHTML(specificationsContent)
                    ) : (
                      <p className="text-gray-500 italic">暂无技术规格</p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'applications' && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-tech mb-4">应用领域</h3>
                  <div className="text-gray-600">
                    {applicationsContent ? (
                      renderHTML(applicationsContent)
                    ) : (
                      <p className="text-gray-500 italic">暂无应用领域</p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'support' && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-tech mb-4">支持信息</h3>
                  <div className="text-gray-600">
                    {supportInfoContent ? (
                      renderHTML(supportInfoContent)
                    ) : (
                      <p className="text-gray-500 italic">暂无支持信息</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="mt-8 p-6 bg-white rounded-lg shadow-md">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              此预览页面仅用于查看产品效果，数据尚未保存到服务器。
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => window.close()}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                关闭预览
              </button>
              <Link
                to="/admin/products"
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                返回编辑
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPreview;
